import React, {
  createContext, useState, useContext, useMemo, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { deduplicate } from 'functions/deduplicate';
import { useInitUrlParameters } from 'utils/hooks/useInitUrlParameters';
import { usePopstate } from 'utils/hooks/usePopstate';
import { useUpdateLocationSearchParameters } from 'utils/hooks/useUpdateLocationSearchParameters';
import { FILTER_CATEGORY, OPTIONS_BY_FILTER_CATEGORY } from './constants';

const {
  TREATMENT_TYPES: TREATMENT_TYPES_CATEGORY,
  INTERVENTIONAL_TYPES: INTERVENTIONAL_TYPES_CATEGORY,
} = FILTER_CATEGORY;

const ALL_CATEGORIES = Object.values(FILTER_CATEGORY);

const MatchesFilterContext = createContext(null);

const stringifyFilters = (filters) => JSON.stringify(Object.fromEntries(
  Object.values(ALL_CATEGORIES).map((category) => [
    category,
    [...(filters[category] ?? [])].sort(),
  ]),
));

const adjustFilterCategoryValue = (value) => deduplicate(Array.isArray(value) ? value : [value]);

const adjustFiltersValue = (nextValue) => {
  const filters = Object.fromEntries(ALL_CATEGORIES.map((category) => [
    category,
    adjustFilterCategoryValue(nextValue[category] ?? []),
  ]));

  return filters;
};

const urlSearchParametersToFilters = (urlSearchParameters) => adjustFiltersValue(urlSearchParameters);

const filtersToUrlSearchParameters = (filters) => Object.fromEntries(
  Object.entries(filters).map(([category, value]) => [
    category,
    OPTIONS_BY_FILTER_CATEGORY[category].length === value.length ? [] : value,
  ]),
);

export const MatchesFilterProvider = ({ children }) => {
  const { query: initUrlSearchParameters } = useInitUrlParameters();

  const [filters, setFilters] = useState(() => urlSearchParametersToFilters(initUrlSearchParameters));

  const setTreatmentTypes = useCallback((arg) => {
    setFilters((currentFilters) => {
      const value = typeof arg === 'function' ? arg(currentFilters[TREATMENT_TYPES_CATEGORY]) : arg;
      return { ...currentFilters, [TREATMENT_TYPES_CATEGORY]: adjustFilterCategoryValue(value) };
    });
  }, []);

  const setInterventionalTypes = useCallback((arg) => {
    setFilters((currentFilters) => {
      const value = typeof arg === 'function' ? arg(currentFilters[INTERVENTIONAL_TYPES_CATEGORY]) : arg;
      return { ...currentFilters, [INTERVENTIONAL_TYPES_CATEGORY]: adjustFilterCategoryValue(value) };
    });
  }, []);

  const toggleInterventionalType = useCallback((typeValue) => {
    setInterventionalTypes((prev) => (prev.includes(typeValue)
      ? prev.filter((item) => item !== typeValue)
      : [...prev, typeValue]));
  }, [setInterventionalTypes]);

  const value = useMemo(() => ({
    treatmentTypes: filters[TREATMENT_TYPES_CATEGORY],
    interventionalTypes: filters[INTERVENTIONAL_TYPES_CATEGORY],
    toggleInterventionalType,
    setInterventionalTypes,
    setTreatmentTypes,
    filters,
    setFilters: (arg) => {
      setFilters((currentFilters) => {
        const nextFilters = adjustFiltersValue(typeof arg === 'function' ? arg(currentFilters) : arg);
        return { ...currentFilters, ...nextFilters };
      });
    },
  }), [filters, toggleInterventionalType, setInterventionalTypes, setTreatmentTypes]);

  return (
    <MatchesFilterContext.Provider value={value}>
      {children}
    </MatchesFilterContext.Provider>
  );
};

MatchesFilterProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useMatchesFilter = () => {
  const context = useContext(MatchesFilterContext);
  if (context === null) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
};

export const useMatchesFilterLocation = () => {
  const { filters, setFilters } = useMatchesFilter();
  const replaceLocationSearchParameters = useUpdateLocationSearchParameters({ isPushMethod: false });

  const filtersString = stringifyFilters(filters);

  usePopstate(useCallback(() => {
    setFilters(urlSearchParametersToFilters(queryString.parseUrl(window.location.search)));
  }, [setFilters]));

  useEffect(() => {
    replaceLocationSearchParameters(filtersToUrlSearchParameters(JSON.parse(filtersString)));
  }, [filtersString, replaceLocationSearchParameters]);
};
